import {
  PrimaryButton,
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getCurrentQuote,
  getUsablePaymentMethod,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  TKey,
  tPaymentMethods,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { CheckoutMethod, PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../proposalContext'
import { DirectDebitSetup } from './DirectDebitSetup'

export interface CheckoutDirectDebitProps {
  checkoutMethod: CheckoutMethod
  config: PaymentScheduleConfig
}

const tKey = TKey('components.checkout-direct-debit')

export const CheckoutDirectDebit: FunctionComponent<CheckoutDirectDebitProps> = ({
  checkoutMethod,
  config,
}) => {
  const { apiClient } = usePrincipal()

  const { t } = useWriskTranslation()

  const { product } = useProduct()
  const { proposal, setProposal } = useProposal()
  const { paymentMethods } = usePaymentMethods()

  const quote = getCurrentQuote(proposal)
  const paymentMethod = getUsablePaymentMethod(
    paymentMethods,
    PaymentMethodType.BACS_DEBIT,
  )

  const premiums = useQuotePremiums(quote, product, config.type)

  const checkoutType = getCheckoutType(premiums)

  const onCheckout = useApiErrorHandlingAsyncCallback(async () => {
    setProposal(
      await apiClient.bindProposal(proposal.proposalCode, {
        paymentMethodType: PaymentMethodType.BACS_DEBIT,
      }),
    )
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('title'))}</SectionTitle>
        <SectionDescription>
          {t(
            [
              tKey('description', config.type, checkoutType),
              tKey('description', config.type),
              tKey('description'),
            ],
            {
              statement: t(tPaymentMethods('statement')),
            },
          )}
        </SectionDescription>
        <SectionContent>
          <DirectDebitSetup mandate={paymentMethod} context={checkoutMethod} />
        </SectionContent>
      </Section>
      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        disabled={!paymentMethod}
        onClick={onCheckout.execute}
        loading={onCheckout.loading}
      >
        {t([
          tKey('action', config.type, checkoutType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getCheckoutType = (premiums: QuotePremiums): 'PAY' | 'BIND' =>
  premiums.totals.discounted > 0.01 ? 'PAY' : 'BIND'
